<template>
  <div class="auth-shell">
    <!-- ============================================================ -->
    <!--   LEFT: brand + trust + payment rails                        -->
    <!-- ============================================================ -->
    <aside class="auth-left">
      <!-- Layered background: gradient + orbs + subtle grid overlay. -->
      <div class="left-bg-orbs">
        <span class="orb orb-a"></span>
        <span class="orb orb-b"></span>
        <span class="orb orb-c"></span>
      </div>
      <div class="left-bg-grid" aria-hidden="true"></div>

      <div class="left-inner">
        <!-- Row 1: brand mark + secure badge (top nav). -->
        <div class="left-top">
          <div class="brand">
            <div class="brand-mark">
              <span class="brand-mark-text">SPS</span>
            </div>
            <div class="brand-copy">
              <div class="brand-name">SPS</div>
              <div class="brand-tag">Secure Payment System</div>
            </div>
          </div>
          <div class="secure-badge" title="Encrypted, session-scoped access">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8">
              <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" stroke-linecap="round" stroke-linejoin="round"/>
              <path d="M9 12l2 2 4-4" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
            <span>Secure sign-in</span>
          </div>
        </div>

        <!-- Row 2: editorial hero. -->
        <div class="hero">
          <div class="hero-eyebrow">
            <span class="eyebrow-dot"></span>
            Operator Portal
          </div>
          <h1 class="hero-title">
            Where every rupee
            <span class="accent">finds its place.</span>
          </h1>
          <p class="hero-sub">
            The single-pane workspace behind your merchants, branches,
            settlements and reconciliation — designed to stay quiet while
            your operations move fast.
          </p>

          <!--
            Feature grid — institutional-tier language. Each card names a
            capability that genuinely lives in the product; the framing is
            designed to signal financial-ops seriousness without SLA or
            volume claims.
          -->
          <div class="feature-grid">
            <div class="feature-card">
              <div class="feature-icon">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8">
                  <path d="M3 21h18M5 21V7l7-4 7 4v14M9 9h1M9 13h1M9 17h1M14 9h1M14 13h1M14 17h1" stroke-linecap="round" stroke-linejoin="round"/>
                </svg>
              </div>
              <div class="feature-body">
                <div class="feature-title">Distributed settlement engine</div>
                <div class="feature-sub">Multi-branch payouts with precise attribution and reconciled commission ledgers.</div>
              </div>
            </div>

            <div class="feature-card">
              <div class="feature-icon">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8">
                  <path d="M21 12a9 9 0 01-15 6.7L3 21m0-9a9 9 0 0115-6.7L21 3" stroke-linecap="round" stroke-linejoin="round"/>
                  <path d="M21 3v6h-6M3 21v-6h6" stroke-linecap="round" stroke-linejoin="round"/>
                </svg>
              </div>
              <div class="feature-body">
                <div class="feature-title">Bank-statement reconciliation</div>
                <div class="feature-sub">Automated variance, orphan and drift detection across every statement source.</div>
              </div>
            </div>

            <div class="feature-card">
              <div class="feature-icon">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8">
                  <rect x="4" y="11" width="16" height="10" rx="2"/>
                  <path d="M8 11V7a4 4 0 018 0v4" stroke-linecap="round"/>
                </svg>
              </div>
              <div class="feature-body">
                <div class="feature-title">Institution-grade authentication</div>
                <div class="feature-sub">TOTP-secured sign-in with session-scoped tokens and operator-tier access control.</div>
              </div>
            </div>

            <div class="feature-card">
              <div class="feature-icon">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8">
                  <path d="M14 3H6a2 2 0 00-2 2v14a2 2 0 002 2h12a2 2 0 002-2V9l-6-6z" stroke-linecap="round" stroke-linejoin="round"/>
                  <path d="M14 3v6h6M8 13h8M8 17h5" stroke-linecap="round"/>
                </svg>
              </div>
              <div class="feature-body">
                <div class="feature-title">Regulatory audit ledger</div>
                <div class="feature-sub">Immutable, searchable lineage of every override, callback and manual edit.</div>
              </div>
            </div>
          </div>
        </div>

        <!-- Row 3: payment-rails marquee + editorial pull-quote. -->
        <div class="left-bottom">
          <div class="rails">
            <div class="rails-label">
              <span class="rails-line"></span>
              <span>Runs on the rails you already trust</span>
              <span class="rails-line"></span>
            </div>
            <div class="rails-marquee">
              <div class="rails-track">
                <span class="rail-chip">UPI</span>
                <span class="rail-chip">IMPS</span>
                <span class="rail-chip">NEFT</span>
                <span class="rail-chip">RTGS</span>
                <span class="rail-chip">GPay</span>
                <span class="rail-chip">PhonePe</span>
                <span class="rail-chip">Paytm</span>
                <!-- Duplicate for seamless loop. -->
                <span class="rail-chip">UPI</span>
                <span class="rail-chip">IMPS</span>
                <span class="rail-chip">NEFT</span>
                <span class="rail-chip">RTGS</span>
                <span class="rail-chip">GPay</span>
                <span class="rail-chip">PhonePe</span>
                <span class="rail-chip">Paytm</span>
              </div>
            </div>
          </div>

          <div class="pull-quote">
            <svg class="quote-mark" viewBox="0 0 24 24" fill="currentColor">
              <path d="M7 7h4v4H7c0 2 1 3 3 3v3c-4 0-6-2-6-6V7zm10 0h4v4h-4c0 2 1 3 3 3v3c-4 0-6-2-6-6V7z"/>
            </svg>
            <p>Fewer surprises. Cleaner books. Calmer teams.</p>
          </div>
        </div>
      </div>
    </aside>

    <!-- ============================================================ -->
    <!--   RIGHT: focused login form                                   -->
    <!-- ============================================================ -->
    <main class="auth-right">
      <div class="form-wrap">
        <div class="form-head-mobile">
          <div class="brand-mark small">
            <span class="brand-mark-text">SPS</span>
          </div>
          <div class="brand-copy">
            <div class="brand-name">SPS</div>
            <div class="brand-tag">Secure Payment System</div>
          </div>
        </div>

        <div class="form-card">
          <div class="form-head">
            <div class="eyebrow">Portal</div>
            <h2 v-if="!totpRequired">Sign in to continue</h2>
            <h2 v-else>Verify it's you</h2>
            <p v-if="!totpRequired" class="form-sub">Enter your credentials to access your dashboard.</p>
            <p v-else class="form-sub">Enter the 6-digit code from your authenticator app.</p>
          </div>

          <transition name="fade">
            <div v-if="error" class="alert alert-error">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <circle cx="12" cy="12" r="10" />
                <path d="M12 8v4M12 16h.01" stroke-linecap="round" />
              </svg>
              <span>{{ error }}</span>
              <button type="button" class="alert-close" @click="handleError">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2">
                  <path d="M6 18L18 6M6 6l12 12" stroke-linecap="round" />
                </svg>
              </button>
            </div>
          </transition>

          <form @submit.prevent="submitForm" class="form-body" novalidate>
            <!-- Username -->
            <div v-if="!totpRequired" class="field">
              <label for="username">Username</label>
              <div class="input-wrap">
                <svg class="input-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8">
                  <circle cx="12" cy="8" r="4" />
                  <path d="M4 21a8 8 0 0116 0" stroke-linecap="round" />
                </svg>
                <input
                  type="text"
                  id="username"
                  v-model.trim="username"
                  placeholder="your.username"
                  autocomplete="username"
                  :disabled="isLoading"
                />
              </div>
            </div>

            <!-- Password -->
            <div v-if="!totpRequired" class="field">
              <label for="password">Password</label>
              <div class="input-wrap">
                <svg class="input-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8">
                  <rect x="4" y="11" width="16" height="10" rx="2" />
                  <path d="M8 11V7a4 4 0 018 0v4" stroke-linecap="round" />
                </svg>
                <input
                  :type="showPassword ? 'text' : 'password'"
                  id="password"
                  v-model.trim="password"
                  placeholder="Your password"
                  autocomplete="current-password"
                  :disabled="isLoading"
                />
                <button
                  type="button"
                  class="eye-btn"
                  @click="showPassword = !showPassword"
                  :title="showPassword ? 'Hide password' : 'Show password'"
                  tabindex="-1"
                >
                  <svg v-if="!showPassword" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8">
                    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8S1 12 1 12z" stroke-linecap="round" stroke-linejoin="round" />
                    <circle cx="12" cy="12" r="3" />
                  </svg>
                  <svg v-else viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8">
                    <path d="M17.94 17.94A10.94 10.94 0 0112 20c-7 0-11-8-11-8a19.77 19.77 0 015.06-6.06M9.9 4.24A10.94 10.94 0 0112 4c7 0 11 8 11 8a19.5 19.5 0 01-3.17 4.19M14.12 14.12A3 3 0 019.88 9.88" stroke-linecap="round" stroke-linejoin="round" />
                    <path d="M1 1l22 22" stroke-linecap="round" />
                  </svg>
                </button>
              </div>
            </div>

            <!-- 2FA -->
            <div v-if="totpRequired" class="field">
              <label for="code">Authentication code</label>
              <div class="input-wrap">
                <svg class="input-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8">
                  <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" stroke-linecap="round" stroke-linejoin="round" />
                  <path d="M9 12l2 2 4-4" stroke-linecap="round" stroke-linejoin="round" />
                </svg>
                <input
                  type="text"
                  id="code"
                  inputmode="numeric"
                  maxlength="6"
                  autocomplete="one-time-code"
                  placeholder="123456"
                  v-model.trim="code"
                  ref="codeInput"
                  :disabled="isLoading"
                  class="input-otp"
                />
              </div>
              <p v-if="totpError" class="field-error">{{ totpError }}</p>
              <p v-else class="field-hint">Open your authenticator app and enter the current 6-digit code.</p>
            </div>

            <!-- Keep me logged in (7 days) — credentials step only -->
            <div v-if="!totpRequired" class="field remember-field">
              <label class="remember-label">
                <input type="checkbox" v-model="rememberMe" :disabled="isLoading" />
                <span>Keep me logged in for 7 days</span>
              </label>
            </div>

            <p v-if="!formIsValid" class="field-error field-error-top">
              Please enter a valid username and password.
            </p>

            <button
              type="submit"
              class="btn-submit"
              :disabled="isLoading"
            >
              <span v-if="!isLoading">{{ submitButtonCaption }}</span>
              <span v-else class="btn-loading">
                <span class="spinner-mini"></span>
                <span>{{ totpRequired ? 'Verifying…' : 'Signing in…' }}</span>
              </span>
            </button>

            <div v-if="totpRequired" class="switch-row">
              <button type="button" class="link-btn" @click="cancelTotp">
                &larr; Back to sign-in
              </button>
            </div>
          </form>
        </div>

        <div class="form-foot">
          <div class="foot-secure">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8">
              <rect x="4" y="11" width="16" height="10" rx="2" />
              <path d="M8 11V7a4 4 0 018 0v4" stroke-linecap="round" />
            </svg>
            <span>256-bit encrypted. Session-scoped access.</span>
          </div>
        </div>
      </div>
    </main>
  </div>
</template>

<script>
export default {
  data() {
    return {
      username: "",
      password: "",
      code: "",
      rememberMe: false,
      showPassword: false,
      totpRequired: false,
      totpError: null,
      formIsValid: true,
      mode: "login",
      isLoading: false,
      error: null,
    };
  },
  computed: {
    submitButtonCaption() {
      if (this.totpRequired) return "Verify & continue";
      if (this.mode === "login") return "Sign in";
      return "Sign up";
    },
  },
  // Toggle a body class only while the sign-in page is mounted. The
  // shared `blank` layout paints its wrapper `bg-gray-50` (Tailwind); the
  // matching selector in the non-scoped <style> below overrides that so
  // any pixels around the shell adopt the login's navy-to-cream chrome.
  // Removed on unmount so other blank pages (/pay/:refID, /callback, …)
  // keep their original background.
  mounted() {
    document.body.classList.add("is-auth-page");
  },
  beforeUnmount() {
    document.body.classList.remove("is-auth-page");
  },
  methods: {
    async submitForm() {
      this.formIsValid = true;
      if (!this.totpRequired && (this.username === "" || this.password === "")) {
        this.formIsValid = false;
        return;
      }
      if (this.totpRequired && this.code.length !== 6) {
        this.totpError = "Enter the full 6-digit code.";
        return;
      }

      this.isLoading = true;
      this.error = null;

      const actionPayload = {
        username: this.username,
        password: this.password,
        ...(this.totpRequired && this.code ? { code: this.code } : {}),
        rememberMe: this.rememberMe,
      };

      try {
        if (this.mode === "login") {
          await this.$store.dispatch("login", actionPayload);
        } else {
          await this.$store.dispatch("signup", actionPayload);
        }
        const redirectUrl = "/" + (this.$route.query.redirect || "dashboard");
        this.$router.replace(redirectUrl);
      } catch (err) {
        // Server requested a 2FA code — switch the form into step-up mode.
        if (err && err.totpRequired) {
          this.totpRequired = true;
          this.totpError = err.totpInvalid ? (err.message || "Invalid two-factor code.") : null;
          this.code = "";
          this.$nextTick(() => {
            try {
              this.$refs.codeInput && this.$refs.codeInput.focus();
            } catch (_) {
              /* focus is best-effort */
            }
          });
          this.isLoading = false;
          return;
        }
        this.error =
          (err && err.response && err.response.data && err.response.data.message) ||
          (err && err.message) ||
          "Failed to authenticate, try later.";
      }

      this.isLoading = false;
    },
    cancelTotp() {
      this.totpRequired = false;
      this.totpError = null;
      this.code = "";
    },
    handleError() {
      this.error = null;
    },
  },
};
</script>

<!--
  Non-scoped block — intentional. The `blank` layout paints its wrapper
  with Tailwind's `bg-gray-50`, and a scoped selector can't reach past
  the login component's own tree to override that. Gated by
  `body.is-auth-page` (toggled in mount/unmount above) so the rules
  only apply on the sign-in route.

  Design goals:
    · Cinematic navy → cream ambient that spotlights the top-left logo
      area (radial highlight anchored roughly where the brand mark
      renders inside .auth-left).
    · A second cool-navy highlight in the bottom-right corner reinforces
      the financial-management tone (stability, trust) without competing
      with the marketing pane.
    · Both html + body + the layout's own wrapper div get the same
      backdrop, so any pixel outside .auth-shell (scrollbar gutter,
      subpixel rounding, iOS bounce) stays on-brand.
-->
<style>
/*
  Outer chrome (body + html + blank layout wrapper) — soft warm-cream
  wash that reads as a single continuous surface with the form pane.
  Extra intermediate color stops in the base linear gradient prevent
  the visible banding older monitors used to show on wide low-contrast
  gradients. Two very-low-opacity radial highlights (gold, coral) give
  it warmth without pulling attention. `fixed` attachment keeps the
  gradient anchored to the viewport so any bounce / scrollbar gutter
  reveals the same look.
*/
body.is-auth-page,
html:has(body.is-auth-page) {
  background:
    radial-gradient(circle at 18% 12%, rgba(255, 215, 168, 0.32), transparent 60%),
    radial-gradient(circle at 84% 88%, rgba(199, 52, 67, 0.05), transparent 55%),
    linear-gradient(
      135deg,
      #fbfaf7 0%,
      #f8f4ea 30%,
      #f4f0e5 55%,
      #ede5d2 80%,
      #e8dfc9 100%
    );
  background-attachment: fixed;
  margin: 0;
}
/* Match the blank layout's own wrapper — it sits between body and
   .auth-shell and would otherwise cover the body backdrop. The selector
   name-checks the exact Tailwind class combo used in layouts/blank.vue;
   if that layout is refactored, this rule turns into a no-op safely. */
body.is-auth-page .min-h-screen.w-full {
  background: transparent !important;
}
</style>

<style scoped>
/* ==================== Shell ==================== */
.auth-shell {
  /* True full-screen fill: full width kills the "line" that used to appear
     where a centered shell met the outer chrome, and `100dvh` handles the
     iOS URL bar wobble that `100vh` alone would cause on mobile.
     Background is intentionally transparent — the outer chrome (body-level
     gradient in the non-scoped style block above) is now the source of
     truth for the surround, so the shell can't paint a hard seam over it. */
  width: 100%;
  min-height: 100vh;
  min-height: 100dvh;
  display: flex;
  background: transparent;
  color: #1b2a47;
  font-family: inherit;
}

/* ==================== Left: marketing showcase ==================== */
/*
  Right edge intentionally dissolves — the previous split had a hard
  vertical seam where navy hit cream. A tail-off in the linear gradient
  (`#253758` → `#3d4c76` → transparent-ish at 100%) blends the marketing
  pane into the outer chrome instead of stopping dead. Long-tail stop
  spread also avoids color-banding.
*/
.auth-left {
  flex: 1.15;
  min-width: 0;
  position: relative;
  overflow: hidden;
  background:
    linear-gradient(
      100deg,
      #14213d 0%,
      #1b2a47 35%,
      #253758 72%,
      rgba(55, 74, 112, 0.62) 100%
    );
  color: #fff;
  display: flex;
  padding: 44px 56px;
}
/*
  Feathered right edge — right-anchored overlay that gradually lifts the
  navy toward the outer chrome's cream tone over the last ~120px. The
  fade is transparent → cream (never darker), so the seam dissolves into
  a soft dusk band instead of a knife-cut. z-index kept at 0 so the
  marketing copy (.left-inner, z-index:1) stays fully readable.
*/
.auth-left::after {
  content: "";
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  width: 140px;
  pointer-events: none;
  background: linear-gradient(
    90deg,
    transparent 0%,
    rgba(244, 240, 229, 0.06) 55%,
    rgba(244, 240, 229, 0.32) 100%
  );
  z-index: 0;
}
.left-bg-orbs {
  position: absolute;
  inset: 0;
  overflow: hidden;
  pointer-events: none;
}
.orb {
  position: absolute;
  border-radius: 999px;
  filter: blur(90px);
  opacity: 0.4;
  animation: orbFloat 18s ease-in-out infinite;
}
.orb-a { width: 520px; height: 520px; background: #3a5285; top: -160px; right: -140px; }
.orb-b { width: 360px; height: 360px; background: #c73443; bottom: -120px; left: -80px; opacity: 0.24; animation-delay: -6s; }
.orb-c { width: 300px; height: 300px; background: #4b6bad; top: 42%; left: 28%; opacity: 0.2; animation-delay: -12s; }
@keyframes orbFloat {
  0%, 100% { transform: translate3d(0, 0, 0); }
  50%      { transform: translate3d(0, 22px, 0); }
}

/* Subtle grid overlay — adds structure without competing with the copy. */
.left-bg-grid {
  position: absolute;
  inset: 0;
  pointer-events: none;
  background-image:
    linear-gradient(rgba(255,255,255,0.04) 1px, transparent 1px),
    linear-gradient(90deg, rgba(255,255,255,0.04) 1px, transparent 1px);
  background-size: 44px 44px;
  mask-image: radial-gradient(ellipse at center, #000 25%, transparent 78%);
  -webkit-mask-image: radial-gradient(ellipse at center, #000 25%, transparent 78%);
}

.left-inner {
  position: relative;
  z-index: 1;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  gap: 32px;
  width: 100%;
  max-width: 620px;
}

/* ---------- Top row: brand + secure badge ---------- */
.left-top {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
}
.brand {
  display: flex;
  align-items: center;
  gap: 14px;
}
.brand-mark {
  width: 44px; height: 44px;
  border-radius: 12px;
  overflow: hidden;
  display: flex; align-items: center; justify-content: center;
  box-shadow: 0 8px 20px -8px rgba(217, 70, 84, 0.55);
  flex-shrink: 0;
  background: transparent;
}
.brand-mark {
  background: #d94654;
}
.brand-mark-text {
  font-weight: 800;
  font-size: 15px;
  letter-spacing: 0.5px;
  color: #fff;
}
.brand-mark.small .brand-mark-text { font-size: 13px; }
.brand-mark img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}
.brand-mark.small { width: 36px; height: 36px; border-radius: 10px; }
.brand-copy { display: flex; flex-direction: column; gap: 2px; }
.brand-name {
  font-size: 17px;
  font-weight: 700;
  letter-spacing: -0.01em;
  line-height: 1;
}
.brand-tag {
  font-size: 11px;
  color: rgba(255, 255, 255, 0.62);
  letter-spacing: 0.03em;
  line-height: 1;
  text-transform: uppercase;
  font-weight: 600;
}
.secure-badge {
  display: inline-flex;
  align-items: center;
  gap: 7px;
  padding: 7px 12px;
  border-radius: 999px;
  border: 1px solid rgba(255,255,255,0.14);
  background: rgba(255,255,255,0.06);
  backdrop-filter: blur(6px);
  -webkit-backdrop-filter: blur(6px);
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.02em;
  color: rgba(255,255,255,0.82);
}
.secure-badge svg { width: 13px; height: 13px; color: #ffd7a8; }

/* ---------- Hero ---------- */
.hero {
  display: flex;
  flex-direction: column;
  justify-content: center;
  flex: 1;
}
.hero-eyebrow {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  font-size: 10.5px;
  font-weight: 700;
  letter-spacing: 0.18em;
  text-transform: uppercase;
  color: rgba(255, 255, 255, 0.68);
  margin-bottom: 18px;
  padding: 5px 10px;
  border-radius: 999px;
  background: rgba(255,255,255,0.06);
  border: 1px solid rgba(255,255,255,0.1);
  align-self: flex-start;
}
.eyebrow-dot {
  width: 6px; height: 6px;
  border-radius: 999px;
  background: #10b981;
  box-shadow: 0 0 0 3px rgba(16,185,129,0.25);
  animation: dotPulse 2.2s ease-in-out infinite;
}
@keyframes dotPulse {
  0%, 100% { box-shadow: 0 0 0 3px rgba(16,185,129,0.25); }
  50%      { box-shadow: 0 0 0 6px rgba(16,185,129,0.05); }
}
.hero-title {
  font-size: 50px;
  line-height: 1.06;
  font-weight: 800;
  letter-spacing: -0.03em;
  margin: 0 0 22px;
  max-width: 580px;
}
.hero-title .accent {
  background: linear-gradient(135deg, #f5c8ce 0%, #ffd7a8 55%, #fff2c2 100%);
  -webkit-background-clip: text;
  background-clip: text;
  color: transparent;
  display: inline-block;
}
.hero-sub {
  font-size: 15.5px;
  line-height: 1.65;
  color: rgba(255, 255, 255, 0.72);
  max-width: 520px;
  margin: 0 0 32px;
  font-weight: 400;
}

/* ---------- Feature grid ---------- */
.feature-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
  max-width: 580px;
}
.feature-card {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 14px 14px;
  border-radius: 14px;
  background: rgba(255,255,255,0.045);
  border: 1px solid rgba(255,255,255,0.09);
  backdrop-filter: blur(8px);
  -webkit-backdrop-filter: blur(8px);
  transition: transform 0.2s ease, background 0.2s ease, border-color 0.2s ease;
}
.feature-card:hover {
  transform: translateY(-1px);
  background: rgba(255,255,255,0.07);
  border-color: rgba(255,255,255,0.16);
}
.feature-icon {
  width: 32px; height: 32px;
  border-radius: 9px;
  background: linear-gradient(135deg, rgba(255,215,168,0.18), rgba(255,242,194,0.06));
  border: 1px solid rgba(255,215,168,0.24);
  display: inline-flex; align-items: center; justify-content: center;
  color: #ffd7a8;
  flex-shrink: 0;
}
.feature-icon svg { width: 16px; height: 16px; }
.feature-body { display: flex; flex-direction: column; gap: 2px; min-width: 0; }
.feature-title {
  font-size: 13px;
  font-weight: 700;
  color: #fff;
  letter-spacing: -0.005em;
}
.feature-sub {
  font-size: 11.5px;
  line-height: 1.4;
  color: rgba(255,255,255,0.6);
  font-weight: 400;
}

/* ---------- Bottom: payment rails + pull quote ---------- */
.left-bottom {
  display: flex;
  flex-direction: column;
  gap: 24px;
}
.rails {
  display: flex;
  flex-direction: column;
  gap: 10px;
}
.rails-label {
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.18em;
  text-transform: uppercase;
  color: rgba(255,255,255,0.4);
}
.rails-line {
  flex: 1;
  height: 1px;
  background: linear-gradient(90deg, transparent, rgba(255,255,255,0.14), transparent);
}
.rails-marquee {
  position: relative;
  overflow: hidden;
  mask-image: linear-gradient(90deg, transparent 0%, #000 12%, #000 88%, transparent 100%);
  -webkit-mask-image: linear-gradient(90deg, transparent 0%, #000 12%, #000 88%, transparent 100%);
}
.rails-track {
  display: inline-flex;
  gap: 10px;
  animation: railScroll 28s linear infinite;
  will-change: transform;
}
@keyframes railScroll {
  from { transform: translateX(0); }
  to   { transform: translateX(-50%); }
}
.rail-chip {
  display: inline-flex;
  align-items: center;
  padding: 8px 16px;
  border-radius: 999px;
  background: rgba(255,255,255,0.05);
  border: 1px solid rgba(255,255,255,0.1);
  font-size: 12px;
  font-weight: 700;
  color: #fff;
  letter-spacing: 0.03em;
  white-space: nowrap;
  flex-shrink: 0;
}

.pull-quote {
  display: flex;
  gap: 12px;
  padding: 16px 18px;
  border-radius: 14px;
  background: linear-gradient(135deg, rgba(255,215,168,0.08), rgba(199,52,67,0.05));
  border: 1px solid rgba(255,215,168,0.14);
  position: relative;
}
.pull-quote .quote-mark {
  width: 18px; height: 18px;
  color: rgba(255,215,168,0.7);
  flex-shrink: 0;
  margin-top: 2px;
}
.pull-quote p {
  margin: 0;
  font-size: 15px;
  font-weight: 600;
  line-height: 1.4;
  color: rgba(255,255,255,0.88);
  letter-spacing: -0.005em;
  font-style: italic;
}

/* ==================== Right: form ==================== */
/*
  The right pane no longer paints its own gradient — the outer body-level
  wash (see the non-scoped <style> block above) is the single source of
  truth for the cream side. This kills every seam that used to be visible
  where the pane's gradient met the outer chrome. Only the faint grid
  overlay is kept, on a ::before layer that lets the underlying wash pass
  through.
*/
.auth-right {
  flex: 1;
  min-width: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 40px 32px;
  position: relative;
  overflow: hidden;
  background: transparent;
}
.auth-right::before {
  content: "";
  position: absolute;
  inset: 0;
  pointer-events: none;
  background-image:
    linear-gradient(rgba(27, 42, 71, 0.035) 1px, transparent 1px),
    linear-gradient(90deg, rgba(27, 42, 71, 0.035) 1px, transparent 1px);
  background-size: 44px 44px;
  mask-image: radial-gradient(ellipse at center, #000 30%, transparent 80%);
  -webkit-mask-image: radial-gradient(ellipse at center, #000 30%, transparent 80%);
}
.form-wrap {
  width: 100%;
  max-width: 440px;
  display: flex;
  flex-direction: column;
  gap: 18px;
  position: relative;
  z-index: 1;
}

.form-head-mobile {
  display: none;
  align-items: center;
  gap: 12px;
}
.form-head-mobile .brand-copy { color: #1b2a47; }
.form-head-mobile .brand-tag { color: #64748b; }

.form-card {
  background: #fff;
  border: 1px solid #ede9df;
  border-radius: 18px;
  padding: 32px 30px;
  /* Deeper, warmer shadow so the card lifts off the textured background
     — the old shadow read as flat once the bg gained depth. */
  box-shadow:
    0 32px 60px -30px rgba(27, 42, 71, 0.28),
    0 12px 28px -18px rgba(199, 52, 67, 0.14);
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.form-head { display: flex; flex-direction: column; gap: 4px; }
.eyebrow {
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: #8a8371;
}
.form-head h2 {
  font-size: 22px;
  font-weight: 700;
  color: #1b2a47;
  margin: 4px 0 0;
  line-height: 1.15;
  letter-spacing: -0.01em;
}
.form-sub {
  font-size: 13px;
  color: #64748b;
  margin: 4px 0 0;
  line-height: 1.5;
}

/* ---------- Alert ---------- */
.alert {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 12px;
  border-radius: 10px;
  font-size: 13px;
  font-weight: 500;
}
.alert svg { width: 16px; height: 16px; flex-shrink: 0; }
.alert-error {
  background: #fef2f2;
  border: 1px solid #fecaca;
  color: #991b1b;
}
.alert-close {
  margin-left: auto;
  border: none;
  background: transparent;
  color: currentColor;
  opacity: 0.7;
  cursor: pointer;
  padding: 2px;
  border-radius: 4px;
  display: inline-flex;
  transition: opacity 0.15s;
}
.alert-close svg { width: 14px; height: 14px; }
.alert-close:hover { opacity: 1; }

/* ---------- Form body ---------- */
.form-body {
  display: flex;
  flex-direction: column;
  gap: 14px;
}
.field { display: flex; flex-direction: column; }
.remember-field { margin-top: -2px; }
/* Specificity note: `.field label` (class+element) below outranks a bare
   `.remember-label`, so we scope under `.remember-field` to win and keep
   the flex gap. The margin-right on the checkbox is a belt-and-suspenders
   fallback in case the label ever renders as display:block. */
.remember-field .remember-label {
  display: flex;
  align-items: center;
  font-size: 0.86rem;
  cursor: pointer;
  user-select: none;
}
/* Custom appearance so the box is a fixed element that centers cleanly in
   the flex row (the native control's intrinsic baseline rendered slightly
   off-center) and matches the app theme. Single spacing source =
   margin-right so it holds even if the label ever computes to block. */
.remember-field .remember-label input[type="checkbox"] {
  appearance: none;
  -webkit-appearance: none;
  box-sizing: border-box;
  width: 16px;
  height: 16px;
  margin: 0 8px 0 0;
  border: 1.5px solid #cbd2e0;
  border-radius: 5px;
  background: #fff;
  position: relative;
  flex-shrink: 0;
  cursor: pointer;
  transition: background .15s ease, border-color .15s ease;
}
.remember-field .remember-label input[type="checkbox"]:hover { border-color: #00296f; }
.remember-field .remember-label input[type="checkbox"]:checked { background: #00296f; border-color: #00296f; }
.remember-field .remember-label input[type="checkbox"]:checked::after {
  content: "";
  position: absolute;
  left: 4.5px;
  top: 1px;
  width: 4px;
  height: 8px;
  border: solid #fff;
  border-width: 0 2px 2px 0;
  transform: rotate(45deg);
}
.remember-field .remember-label input[type="checkbox"]:focus-visible {
  outline: none;
  box-shadow: 0 0 0 3px rgba(0, 41, 111, .18);
}
.field label {
  display: block;
  font-size: 12px;
  font-weight: 600;
  color: #334155;
  margin-bottom: 6px;
  letter-spacing: 0.01em;
}
.input-wrap {
  position: relative;
  display: flex;
  align-items: center;
}
.input-icon {
  position: absolute;
  left: 12px;
  width: 16px; height: 16px;
  color: #94a3b8;
  pointer-events: none;
}
.input-wrap input {
  width: 100%;
  padding: 11px 40px 11px 38px;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  font-size: 14px;
  background: #f8fafc;
  color: #0f172a;
  font-family: inherit;
  outline: none;
  transition: border-color 0.15s, background 0.15s, box-shadow 0.15s;
}
.input-wrap input::placeholder { color: #cbd5e1; }
.input-wrap input:focus {
  border-color: #1b2a47;
  background: #fff;
  box-shadow: 0 0 0 4px rgba(27, 42, 71, 0.08);
}
.input-wrap input:disabled {
  background: #f1f5f9;
  color: #94a3b8;
  cursor: not-allowed;
}
.eye-btn {
  position: absolute;
  right: 8px;
  border: none;
  background: transparent;
  cursor: pointer;
  color: #94a3b8;
  width: 28px; height: 28px;
  border-radius: 8px;
  display: inline-flex; align-items: center; justify-content: center;
  transition: all 0.12s;
}
.eye-btn:hover { background: #f1f5f9; color: #475569; }
.eye-btn svg { width: 16px; height: 16px; }

.input-otp {
  font-family: "SFMono-Regular", Menlo, monospace;
  font-size: 20px !important;
  letter-spacing: 0.5em;
  text-align: center;
  font-weight: 700;
  padding-right: 12px !important;
}

.field-hint {
  font-size: 11.5px;
  color: #94a3b8;
  margin: 6px 0 0;
  line-height: 1.4;
}
.field-error {
  font-size: 12px;
  color: #dc2626;
  margin: 6px 0 0;
  font-weight: 500;
}
.field-error-top { margin-top: -4px; }

/* ---------- Submit ---------- */
.btn-submit {
  margin-top: 6px;
  width: 100%;
  padding: 13px 16px;
  border: none;
  border-radius: 12px;
  font-size: 14px;
  font-weight: 600;
  letter-spacing: 0.01em;
  color: #fff;
  background: linear-gradient(135deg, #1b2a47 0%, #2c3e63 100%);
  cursor: pointer;
  transition: all 0.15s;
  box-shadow: 0 10px 24px -10px rgba(27, 42, 71, 0.45);
  font-family: inherit;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}
.btn-submit:hover:not(:disabled) {
  transform: translateY(-1px);
  box-shadow: 0 14px 28px -10px rgba(27, 42, 71, 0.55);
}
.btn-submit:disabled {
  opacity: 0.75;
  cursor: not-allowed;
}
.btn-loading {
  display: inline-flex;
  align-items: center;
  gap: 10px;
}
.spinner-mini {
  width: 14px; height: 14px;
  border-radius: 999px;
  border: 2px solid rgba(255, 255, 255, 0.35);
  border-top-color: #fff;
  animation: spin 0.7s linear infinite;
}
@keyframes spin { to { transform: rotate(360deg); } }

.switch-row {
  display: flex;
  justify-content: center;
  padding-top: 4px;
}
.link-btn {
  border: none;
  background: transparent;
  font-size: 12.5px;
  color: #64748b;
  cursor: pointer;
  font-weight: 500;
  padding: 4px 8px;
  border-radius: 6px;
  font-family: inherit;
  transition: color 0.15s, background 0.15s;
}
.link-btn:hover { color: #1b2a47; background: #f1f5f9; }

/* ---------- Foot ---------- */
.form-foot {
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 4px 4px 0;
  align-items: center;
}
.foot-secure {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  font-size: 11.5px;
  color: #8a8371;
  font-weight: 500;
}
.foot-secure svg { width: 13px; height: 13px; color: #a8a08c; }

.foot-alt {
  font-size: 12.5px;
  color: #64748b;
}
.link-inline {
  color: #1b2a47;
  font-weight: 600;
  text-decoration: none;
  transition: color 0.15s;
}
.link-inline:hover { color: #c73443; }

/* ==================== Transitions ==================== */
.fade-enter-active, .fade-leave-active { transition: opacity 0.2s ease, transform 0.2s ease; }
.fade-enter-from, .fade-leave-to { opacity: 0; transform: translateY(-4px); }

/* ==================== Responsive ==================== */
@media (max-width: 1180px) {
  /* Tablet: keep the marketing pane visible but trim the feature grid to
     one column so the copy stays readable. Font sizes also drop a touch. */
  .auth-left { padding: 36px 40px; }
  .hero-title { font-size: 40px; }
  .feature-grid { grid-template-columns: 1fr; max-width: 460px; }
}
@media (max-width: 900px) {
  /* Below tablet: marketing pane hides. Form fills the screen; the mobile
     brand header covers the identity role the left pane used to play. */
  .auth-shell { flex-direction: column; }
  .auth-left { display: none; }
  .form-head-mobile { display: flex; }
  .auth-right { padding: 32px 20px; }
}
@media (max-width: 480px) {
  .form-card { padding: 24px 20px; border-radius: 14px; }
  .form-head h2 { font-size: 20px; }
}
</style>
